/**
 * Consent banner for session logging (DSGVO Opt-In).
 *
 * Shown once per browser-storage state. Choice is persisted in localStorage:
 *   "session_logging_consent": "granted" | "declined"
 *
 * If choice is missing, `requestConsent()` opens a modal that resolves with
 * the user's decision. Subsequent calls return the stored decision without
 * showing the modal.
 *
 * Module is browser-only — uses localStorage and DOM APIs.
 */

const STORAGE_KEY = "session_logging_consent";

type ConsentState = "granted" | "declined";

/**
 * Returns the current consent state, or null if the user hasn't decided yet.
 */
export function readConsent(): ConsentState | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "granted" || v === "declined") return v;
    return null;
  } catch {
    // localStorage may be blocked in some iframe configs — treat as no consent
    return null;
  }
}

/**
 * Convenience: returns true only if the user has explicitly granted logging.
 * Used by SessionLogger to disable itself when consent is missing or declined.
 */
export function hasLoggingConsent(): boolean {
  return readConsent() === "granted";
}

/**
 * Persists the user's choice. Used by the modal buttons.
 */
function setConsent(state: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, state);
  } catch {
    // localStorage blocked — choice won't persist beyond this session
  }
}

/**
 * If consent is unknown, shows a modal and returns a Promise that resolves with
 * the user's choice. If already decided, resolves immediately with the stored
 * value. Call this before starting the avatar session.
 */
export function requestConsent(): Promise<ConsentState> {
  const existing = readConsent();
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "consent-overlay";
    overlay.innerHTML = `
      <div class="consent-modal" role="dialog" aria-modal="true" aria-labelledby="consent-title">
        <h2 id="consent-title">🔒 Beratungsgespräch aufzeichnen?</h2>
        <p>
          Diese Beratung wird zu Qualitätszwecken aufgezeichnet. Der vollständige
          Gesprächsverlauf wird sicher gespeichert.
        </p>
        <p class="consent-detail">
          Die Aufzeichnung hilft uns, Brian zu verbessern und auf Probleme zu reagieren.
          Du kannst die Beratung trotzdem nutzen, wenn du nicht zustimmst. Dann wird
          nichts gespeichert.
        </p>
        <div class="consent-actions">
          <button type="button" class="consent-btn consent-btn--decline" data-choice="declined">
            Ohne Aufzeichnung
          </button>
          <button type="button" class="consent-btn consent-btn--accept" data-choice="granted">
            Zustimmen &amp; starten
          </button>
        </div>
      </div>
    `;

    const choose = (state: ConsentState) => {
      setConsent(state);
      overlay.remove();
      resolve(state);
    };

    overlay.querySelectorAll<HTMLButtonElement>("[data-choice]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const choice = btn.dataset.choice as ConsentState;
        choose(choice);
      });
    });

    document.body.appendChild(overlay);

    // Focus the accept button by default — keyboard users can still tab to decline
    requestAnimationFrame(() => {
      overlay
        .querySelector<HTMLButtonElement>(".consent-btn--accept")
        ?.focus();
    });
  });
}

/**
 * Test-only: clears the stored choice so the modal shows again on next call.
 * Not used in production code — useful for manual QA.
 */
export function _resetConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
